import { PermitGangMember } from "../../models/PermitGangMember";
import { isToday } from "../../DateService";
import { GangMember } from "../../models/GangMember";
import { GangMemberSignature } from "../../models/GangMemberSignature";
import { Guid } from "guid-typescript";
import { ISSUED, READY_TO_ISSUE } from "../../constants";

function removePermitGangMemberFromPermit(
    permitGangMember: PermitGangMember,
    deletePermitGangMemberCallback: (permitGangMember: PermitGangMember) => void,
    setPermitGangMemberUpdateMessageCallback: (message: string) => void,
    permitStatus: string
) {
    const permitGangMemberName = permitGangMember.gangMember.firstName + " " + permitGangMember.gangMember.lastName;

    const canDelete = canDeleteGangMember(permitGangMember, permitStatus);
    if (!canDelete) {
        setPermitGangMemberUpdateMessageCallback(
            `${permitGangMemberName} has already signed or marked as absent before and cannot be removed.`
        );
        return;
    }

    //If permit state is ready to issue the gangmember is deleted no matter if already signed.
    //If there is any signature, the signatures will be deleted as well
    deletePermitGangMemberCallback(permitGangMember);
    setPermitGangMemberUpdateMessageCallback(`${permitGangMemberName} removed from gang members list successfully!`);
    return;
}

function markGangMemberAsAbsent(
    permitGangMember: PermitGangMember,
    submitGangMemberAbsenceCallback: (permitGangMemberId: string, absenceSignature: GangMemberSignature) => void,
    setPermitGangMemberUpdateMessageCallback: (message: string) => void,
    permitStatus: string
) {
    const permitGangMemberName = permitGangMember.gangMember.firstName + " " + permitGangMember.gangMember.lastName;

    const canMarkAbsent = canMarkGangMemberAbsent(permitGangMember, permitStatus);
    if (!canMarkAbsent) {
        setPermitGangMemberUpdateMessageCallback(
            `${permitGangMemberName} has already signed or marked as absent today.`
        );
        return;
    }

    const absentSignature = createGangMemberSignature(new Date());
    absentSignature.isAbsent = true;
    submitGangMemberAbsenceCallback(permitGangMember.id, absentSignature);
    setPermitGangMemberUpdateMessageCallback(`Marked ${permitGangMemberName} as absent for today successfully!`);
}

function undoGangMemberAbsence(
    permitGangMember: PermitGangMember,
    deleteGangMemberSignatureCallback: (permitGangMemberId: string, signatureId: string) => void,
    setPermitGangMemberUpdateMessageCallback: (message: string) => void,
    permitStatus: string
) {
    const permitGangMemberName = permitGangMember.gangMember.firstName + " " + permitGangMember.gangMember.lastName;

    const canUndoAbsence = canUndoGangMemberAbsence(permitGangMember, permitStatus);
    if (!canUndoAbsence) {
        setPermitGangMemberUpdateMessageCallback(
            `${permitGangMemberName} is not marked as absent today and cannot undo absence.`
        );
        return;
    }

    const todaySignature = permitGangMember.signatures.find(
        (a) => isToday(a.date) && a.isAbsent && !a.pendingDelete && !a.deleted
    );
    if (todaySignature) {
        deleteGangMemberSignatureCallback(permitGangMember.id, todaySignature.id);
        setPermitGangMemberUpdateMessageCallback(`Gang member absence for ${permitGangMemberName} has been undone.`);
    }
}

function createPermitGangMember(gangMember: GangMember) {
    const gangMemberSignatures: GangMemberSignature[] = [];

    let gangMemberObject: any = { ...gangMember };
    delete gangMemberObject["type"];
    delete gangMemberObject["channels"];

    const permitGangMember: PermitGangMember = {
        id: Guid.create().toString(),
        gangMember: gangMemberObject,
        signatures: gangMemberSignatures,
    };

    return permitGangMember;
}

function createGangMemberSignature(date: Date) {
    const gangMemberSignature: GangMemberSignature = {
        id: Guid.create().toString(),
        date: new Date(date),
        loggedInUserId: "",
        signatureFileKey: "",
        isAbsent: false,
    };
    return gangMemberSignature;
}

function canDeleteGangMember(permitGangMember: PermitGangMember, permitStatus: string): boolean {
    if (permitStatus !== READY_TO_ISSUE) {
        const gangMemberHasAnySign =
            permitGangMember.signatures.filter((s) => !s.pendingDelete && !s.deleted).length > 0;
        if (gangMemberHasAnySign) {
            return false;
        }
    }

    return true;
}

function canMarkGangMemberAbsent(permitGangMember: PermitGangMember, permitStatus: string): boolean {
    if (permitStatus !== ISSUED) {
        return false;
    }

    const canDelete = canDeleteGangMember(permitGangMember, permitStatus);
    if (canDelete) {
        return false;
    }

    const hasGangMemberSignedToday =
        permitGangMember.signatures.filter(
            (a) => isToday(a.date) && (a.dateSigned || a.isAbsent) && !a.pendingDelete && !a.deleted
        ).length > 0;
    if (hasGangMemberSignedToday) {
        return false;
    }

    return true;
}

function canUndoGangMemberAbsence(permitGangMember: PermitGangMember, permitStatus: string): boolean {
    if (permitStatus !== ISSUED) {
        return false;
    }

    const isGangMemberAbsentToday = permitGangMember.signatures.find(
        (a) => isToday(a.date) && a.isAbsent && !a.pendingDelete && !a.deleted
    );
    if (isGangMemberAbsentToday) {
        return true;
    }

    return false;
}

export {
    removePermitGangMemberFromPermit,
    markGangMemberAsAbsent,
    undoGangMemberAbsence,
    createPermitGangMember,
    createGangMemberSignature,
    canDeleteGangMember,
    canMarkGangMemberAbsent,
    canUndoGangMemberAbsence,
};
